import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Globe, ChevronRight, Unlock, ArrowLeft } from 'lucide-react';
import { getBackendUrl } from '../config/api';

const spin = keyframes`
  from { transform: rotateY(0deg); }
  to { transform: rotateY(360deg); }
`;

const Container = styled.div`
  min-height: 100vh;
  width: 100vw;
  background: linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  overflow-y: auto;
`;

const BackButton = styled.button`
  position: absolute;
  top: 20px;
  left: 20px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 10px 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateX(-2px);
  }

  .icon {
    width: 18px;
    height: 18px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const KuutioIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 20px;
  perspective: 1000px;

  .cube {
    width: 100%;
    height: 100%;
    position: relative;
    transform-style: preserve-3d;
    animation: ${spin} 20s linear infinite;

    .face {
      position: absolute;
      width: 80px;
      height: 80px;
      background: linear-gradient(135deg,
        rgba(76, 175, 80, 0.4) 0%,
        rgba(33, 150, 243, 0.4) 100%);
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-radius: 8px;
      backdrop-filter: blur(5px);
    }

    .front { transform: translateZ(40px); }
    .back { transform: rotateY(180deg) translateZ(40px); }
    .right { transform: rotateY(90deg) translateZ(40px); }
    .left { transform: rotateY(-90deg) translateZ(40px); }
    .top { transform: rotateX(90deg) translateZ(40px); }
    .bottom { transform: rotateX(-90deg) translateZ(40px); }
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #4CAF50, #2196F3);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.1rem;
  margin: 0;
`;

const TestBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(76, 175, 80, 0.2);
  border: 1px solid rgba(76, 175, 80, 0.3);
  color: #4CAF50;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  margin-top: 16px;

  .icon {
    width: 16px;
    height: 16px;
  }
`;

const InstanceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
  width: 100%;
  max-width: 1200px;
`;

const InstanceCard = styled.div`
  background: linear-gradient(135deg,
    rgba(255, 255, 255, 0.08) 0%,
    rgba(255, 255, 255, 0.03) 100%);
  border: 2px solid ${props => props.$selected
    ? 'rgba(76, 175, 80, 0.6)'
    : 'rgba(255, 255, 255, 0.15)'};
  border-radius: 16px;
  padding: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  ${props => props.$selected && `
    background: linear-gradient(135deg,
      rgba(76, 175, 80, 0.15) 0%,
      rgba(33, 150, 243, 0.1) 100%);
    box-shadow: 0 0 30px rgba(76, 175, 80, 0.2);
  `}

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(76, 175, 80, 0.4);
  }
`;

const InstanceHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;

  .icon {
    width: 32px;
    height: 32px;
    color: #4CAF50;
  }
`;

const InstanceTitle = styled.h3`
  margin: 0;
  font-size: 1.3rem;
  font-weight: 700;
  color: white;
`;

const InstanceDescription = styled.p`
  margin: 0 0 16px 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.95rem;
  line-height: 1.5;
`;

const ScenarioTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ScenarioTag = styled.span`
  background: ${props => props.$color ? `${props.$color}30` : 'rgba(33, 150, 243, 0.2)'};
  color: ${props => props.$color || '#64B5F6'};
  border: 1px solid ${props => props.$color ? `${props.$color}50` : 'rgba(33, 150, 243, 0.3)'};
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const PinSection = styled.div`
  width: 100%;
  max-width: 400px;
  margin-top: 40px;
  text-align: center;
`;

const PinLabel = styled.label`
  display: block;
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  margin-bottom: 12px;
`;

const PinInput = styled.input`
  width: 100%;
  padding: 16px 20px;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  color: white;
  font-size: 1.5rem;
  text-align: center;
  letter-spacing: 8px;
  font-weight: 600;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #4CAF50;
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.2);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
    letter-spacing: normal;
  }
`;

const EnterButton = styled.button`
  width: 100%;
  padding: 16px;
  margin-top: 20px;
  background: ${props => props.disabled
    ? 'rgba(255, 255, 255, 0.1)'
    : 'linear-gradient(135deg, #4CAF50, #2196F3)'};
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s ease;
  opacity: ${props => props.disabled ? 0.5 : 1};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(76, 175, 80, 0.3);
  }

  .icon {
    width: 20px;
    height: 20px;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: rgba(255, 255, 255, 0.6);

  .icon {
    width: 64px;
    height: 64px;
    margin-bottom: 20px;
    color: rgba(255, 255, 255, 0.3);
  }

  h3 {
    margin: 0 0 12px 0;
    font-size: 1.4rem;
    color: rgba(255, 255, 255, 0.8);
  }

  p {
    margin: 0;
    max-width: 400px;
    line-height: 1.6;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;

  &::after {
    content: '';
    width: 48px;
    height: 48px;
    border: 3px solid rgba(76, 175, 80, 0.3);
    border-top: 3px solid #4CAF50;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

function TestKuutio() {
  const navigate = useNavigate();
  const [instances, setInstances] = useState([]);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadTestInstances();
  }, []);

  const loadTestInstances = async () => {
    try {
      setLoading(true);
      const baseUrl = getBackendUrl();
      const response = await fetch(`${baseUrl}/api/instances/test`);

      if (!response.ok) {
        throw new Error('Failed to load test instances');
      }

      const data = await response.json();
      setInstances(data);

      // Auto-select if only one instance
      if (data.length === 1) {
        setSelectedInstance(data[0]);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error loading test instances:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEnter = () => {
    if (!selectedInstance || !pin) return;

    // Store instance info
    localStorage.setItem('currentInstanceId', selectedInstance.id);
    localStorage.setItem('currentInstanceTitle', selectedInstance.title);

    // Generate a test participant ID if needed
    let participantId = localStorage.getItem('participantId');
    if (!participantId) {
      participantId = crypto.randomUUID();
      localStorage.setItem('participantId', participantId);
    }

    // Navigate to welcome screen (scenario selection)
    navigate('/welcome');
  };

  const handlePinChange = (e) => {
    // Only allow digits and limit to 6 characters
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setPin(value);
  };

  return (
    <Container>
      <BackButton onClick={() => navigate('/')}>
        <ArrowLeft className="icon" />
        Takaisin
      </BackButton>

      <Header>
        <KuutioIcon>
          <div className="cube">
            <div className="face front" />
            <div className="face back" />
            <div className="face right" />
            <div className="face left" />
            <div className="face top" />
            <div className="face bottom" />
          </div>
        </KuutioIcon>
        <Title>Testikuutio</Title>
        <Subtitle>Valitse testattava Kuutio-instanssi</Subtitle>
        <TestBadge>
          <Unlock className="icon" />
          Avoin testikäyttö
        </TestBadge>
      </Header>

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <EmptyState>
          <Globe className="icon" />
          <h3>Virhe ladattaessa</h3>
          <p>{error}</p>
        </EmptyState>
      ) : instances.length === 0 ? (
        <EmptyState>
          <Globe className="icon" />
          <h3>Ei testiinstansseja</h3>
          <p>
            Yhtään Kuutio-instanssia ei ole merkitty testikäyttöön.
            Fasilitaattori voi ottaa instanssin testikäyttöön asetuksista.
          </p>
        </EmptyState>
      ) : (
        <>
          <InstanceGrid>
            {instances.map(instance => (
              <InstanceCard
                key={instance.id}
                $selected={selectedInstance?.id === instance.id}
                onClick={() => setSelectedInstance(instance)}
              >
                <InstanceHeader>
                  <Globe className="icon" />
                  <InstanceTitle>{instance.title}</InstanceTitle>
                </InstanceHeader>

                <InstanceDescription>
                  {instance.description || 'Ei kuvausta'}
                </InstanceDescription>

                {instance.scenarios && instance.scenarios.length > 0 && (
                  <ScenarioTags>
                    {instance.scenarios.map(scenario => (
                      <ScenarioTag key={scenario.id} $color={scenario.color}>
                        {scenario.icon} {scenario.title}
                      </ScenarioTag>
                    ))}
                  </ScenarioTags>
                )}
              </InstanceCard>
            ))}
          </InstanceGrid>

          {selectedInstance && (
            <PinSection>
              <PinLabel>
                Syötä mikä tahansa PIN-koodi (testaus)
              </PinLabel>
              <PinInput
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="123456"
                value={pin}
                onChange={handlePinChange}
                autoFocus
              />
              <EnterButton
                disabled={!pin}
                onClick={handleEnter}
              >
                Siirry Kuutioon
                <ChevronRight className="icon" />
              </EnterButton>
            </PinSection>
          )}
        </>
      )}
    </Container>
  );
}

export default TestKuutio;
